import { useState } from 'react'
import { teams as api } from '@/api/endpoints'
import type { Team } from '@/api/types'
import { Err } from '@/components/Err'
import { Button } from '@/components/ui/button'
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { splitTags } from '@/domain/team'
import { useExpert } from '@/hooks/useExpert'
import { useWorldRefresh } from '@/hooks/useWorld'

// 팀을 만들고 지우는 창(F-84). 팀 화면과 사용자 목록의 `팀 관리` 창이 같은 창을 쓴다 — 같은 일을 두 자리에서
// 다른 모양으로 하지 않게. 둘 다 대화 상자의 본문만이고 `<Dialog>` 는 부르는 쪽이 갖는다.

// 팀의 식별자 규칙(`TeamCreate.name`). 사람은 이름만 적고 식별자는 비우면 백엔드가 정한다 — 고급에서만 직접 적는다.
const TEAM_ID_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/
const TEAM_ID_RULE = '식별자는 영문 소문자·숫자·하이픈(-)만 쓸 수 있습니다.'

/** 팀 생성. `onDone` 은 만든 팀을 받는다 — `팀 관리` 창은 그 팀을 골라진 채로 둔다. */
export function TeamCreateForm({ onClose, onDone }: { onClose: () => void; onDone: (team: Team) => void }) {
  const { on: expert } = useExpert()
  const [name, setName] = useState('')
  const [display, setDisplay] = useState('')
  const [tags, setTags] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  // 고급을 끄면 적어 둔 식별자도 보내지 않는다 — 보이지 않는 값이 신청을 막거나 바꾸지 않게.
  const id = expert ? name.trim() : ''
  const idBad = id !== '' && (!TEAM_ID_RE.test(id) || id.length > 63)
  const ok = display.trim().length > 0 && !idBad
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!ok) return
    setBusy(true)
    setError(null)
    try {
      onDone(await api.create({ ...(id ? { name: id } : {}), display_name: display.trim(), tags: splitTags(tags) }))
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }
  return (
    <form onSubmit={submit} className="grid gap-3">
      <DialogHeader>
        <DialogTitle>팀 생성</DialogTitle>
      </DialogHeader>
      <div className="grid gap-1.5">
        <Label htmlFor="team-display">이름</Label>
        <Input id="team-display" value={display} maxLength={200} disabled={busy} onChange={(e) => setDisplay(e.target.value)} />
      </div>
      {expert ? (
        <div className="grid gap-1.5">
          <Label htmlFor="team-name">식별자</Label>
          <Input
            id="team-name" value={name} placeholder="자동" disabled={busy}
            aria-invalid={idBad || undefined} onChange={(e) => setName(e.target.value)}
          />
          {idBad ? <span className="text-[11px] text-danger-ink">{TEAM_ID_RULE}</span> : null}
        </div>
      ) : null}
      <div className="grid gap-1.5">
        <Label htmlFor="team-tags">태그</Label>
        <Input id="team-tags" value={tags} placeholder="수업, 연구실" disabled={busy} onChange={(e) => setTags(e.target.value)} />
      </div>
      <Err error={error} />
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
          취소
        </Button>
        <Button type="submit" disabled={busy || !ok}>
          {busy ? '생성 중' : '생성'}
        </Button>
      </DialogFooter>
    </form>
  )
}

/**
 * 팀 삭제. 대상의 목록을 보이고 묻는다 — 사용자 목록의 탈퇴와 같은 확인이고, 팀이 하나여도 같은 모양이다.
 * 한 팀씩 차례로 지우고 거절된 팀만 남아 같은 단추가 곧 재시도다. VM 이 남은 팀은 서버가 몇 대인지 싣고 거절한다(`domain/errors.ts`).
 * 거절이 있으면 목록을 곧바로 다시 읽는다 — 지워진 팀이 다음 폴링까지 목록에 남아 있지 않게.
 */
export function TeamDeleteForm({ teams, onClose, onDone }: { teams: Team[]; onClose: () => void; onDone: () => void }) {
  const refreshWorld = useWorldRefresh()
  const [left, setLeft] = useState<string[]>(() => teams.map((t) => t.id))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const targets = teams.filter((t) => left.includes(t.id))
  const remove = async () => {
    setBusy(true)
    setError(null)
    let first: unknown = null
    const failed = new Set<string>()
    for (const t of targets) {
      try {
        await api.remove(t.id)
      } catch (e) {
        if (first === null) first = e
        failed.add(t.id)
      }
    }
    setBusy(false)
    if (first !== null) {
      setError(first)
      setLeft((ids) => ids.filter((id) => failed.has(id)))
      refreshWorld()
      return
    }
    onDone()
  }
  return (
    <>
      <DialogHeader>
        <DialogTitle>팀 삭제</DialogTitle>
      </DialogHeader>
      <ul aria-label="삭제할 팀" className="max-h-60 overflow-y-auto rounded-md border">
        {targets.map((t) => (
          <li key={t.id} className="truncate px-3 py-1.5 text-[13px] font-medium">
            {t.display_name}
          </li>
        ))}
      </ul>
      <p className="text-[13px] font-medium">{targets.length}개 팀을 삭제하시겠습니까?</p>
      <Err error={error} />
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
          취소
        </Button>
        <Button type="button" variant="destructive" disabled={busy || targets.length === 0} onClick={() => void remove()}>
          {busy ? '삭제 중' : '삭제'}
        </Button>
      </DialogFooter>
    </>
  )
}
